import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { usePublicLang, LangToggle } from "../lib/publicLang";

const API = process.env.REACT_APP_BACKEND_URL;

const EXP = {
  fr: [
    { v: "debutant", label: "Débutant (< 6 mois)" },
    { v: "intermediaire", label: "Intermédiaire (6-24 mois)" },
    { v: "semi_pro", label: "Semi-pro (2+ ans, prop firm ou compte réel > $5k)" },
    { v: "pro", label: "Professionnel (gérant, prop desk, fund analyst)" },
  ],
  en: [
    { v: "debutant", label: "Beginner (< 6 months)" },
    { v: "intermediaire", label: "Intermediate (6-24 months)" },
    { v: "semi_pro", label: "Semi-pro (2+ yrs, prop firm or live account > $5k)" },
    { v: "pro", label: "Professional (PM, prop desk, fund analyst)" },
  ],
};

const CAP = {
  fr: [
    { v: "0", label: "Compte demo uniquement" },
    { v: "1k_5k", label: "$1 000 – $5 000" },
    { v: "5k_25k", label: "$5 000 – $25 000" },
    { v: "25k_100k", label: "$25 000 – $100 000" },
    { v: "100k_plus", label: "$100 000 et plus" },
  ],
  en: [
    { v: "0", label: "Demo account only" },
    { v: "1k_5k", label: "$1,000 – $5,000" },
    { v: "5k_25k", label: "$5,000 – $25,000" },
    { v: "25k_100k", label: "$25,000 – $100,000" },
    { v: "100k_plus", label: "$100,000 and above" },
  ],
};

const MKT = {
  fr: ["Forex majeurs", "Forex crosses/exotiques", "Indices (SPX, NAS, GER…)",
       "Métaux (Or, Argent)", "Crypto majeurs (BTC, ETH)", "Actions US",
       "Prop firm challenge (FTMO, Topstep, MFF…)"],
  en: ["Major forex", "Forex crosses/exotics", "Indices (SPX, NAS, GER…)",
       "Metals (Gold, Silver)", "Major crypto (BTC, ETH)", "US stocks",
       "Prop firm challenge (FTMO, Topstep, MFF…)"],
};

const TXT = {
  fr: {
    ackErr: "Vous devez accepter les CGU et la déclaration de risques.",
    selErr: "Veuillez sélectionner votre expérience et capital.",
    okToast: "Candidature enregistrée !",
    dupErr: (em) => `Une candidature avec l'email ${em} existe déjà. Un seul dépôt par email.`,
    invalidErr: "Formulaire incomplet ou invalide. Vérifiez tous les champs.",
    doneTitle: "Candidature reçue !", back: "Retour", home: "Retour à l'accueil",
    doneP1a: "Nous vous répondrons sous ", doneP1b: "48 heures", doneP1c: " à l'email fourni. Les traders sélectionnés recevront un lien d'accès au programme beta.",
    badge: "Programme sur invitation · Accès sur validation",
    h1: "Rejoindre le programme beta ANATRADEX",
    intro1: "Nous acceptons actuellement un nombre limité de ", intro2: "traders semi-pros et professionnels", intro3: " pour tester ANATRADEX en avant-première, à tarif early-bird. Chaque candidature est validée manuellement sous 48 heures. Aucune carte bancaire à ce stade.",
    name: "Nom (public ou pseudo)", namePh: "Ex : Alex M.",
    exp: "Niveau d'expérience", capital: "Capital en compte trading (actuel)", select: "-- Sélectionner --",
    markets: "Marchés principaux (plusieurs choix possibles)",
    broker: "Broker/plateforme principal(e)", brokerPh: "Ex : IC Markets, OANDA, IBKR, FTMO, aucun...",
    why: "Pourquoi voulez-vous tester ANATRADEX ?", whyHint: "(2-3 phrases)",
    whyPh: "Ex : Je passe un challenge FTMO et je cherche à valider mes signaux via un backtest robuste + suivre mon PBO pour éviter le overfit.",
    ack1a: "J'accepte les ", terms: "CGU", privacy: "confidentialité", cookies: "politique cookies",
    ack2a: "Je reconnais avoir lu la ", riskDoc: "Déclaration de risques",
    ack2b: ". Le trading peut entraîner une ", totalLoss: "perte totale du capital",
    ack2c: ". J'ai au moins 18 ans. ANATRADEX est un outil d'analyse, pas un conseil en investissement.",
    submit: "Envoyer ma candidature", footNote: "Réponse sous 48h. Aucun spam. Aucune carte bancaire à ce stade.",
    riskBanner1: "⚠️ AVERTISSEMENT", riskBanner2: " · ANATRADEX est un ", riskBanner3: "outil d'analyse statistique", riskBanner4: " et NE fournit PAS de conseil en investissement. Le trading comporte des risques de perte substantielle — 70-85% des traders retail perdent selon l'ESMA. ANATRADEX n'est pas enregistré CIF/ORIAS.",
    critTitle: "Qui recherchons-nous ? Nos critères, en toute transparence",
    critIntro: "Chaque vague de testeurs est composée pour équilibrer les profils. Voici exactement ce que nous évaluons :",
    crit: [
      ["Expérience de trading", "Du débutant motivé au professionnel — nous privilégions les traders avec au moins 6 mois de pratique réelle."],
      ["Capital en compte", "Pas un critère d'exclusion : les comptes réels (même modestes) donnent des retours plus concrets qu'un compte démo."],
      ["Motivation précise", "2-3 phrases concrètes sur ce que vous voulez valider (challenge prop firm, stratégie à vérifier, suivi PBO...) pèsent plus qu'un long texte vague."],
      ["Diversité des plateformes", "Nous cherchons un mix MT4 / MT5 / cTrader / TradingView pour tester chaque parcours d'import."],
    ],
    critNote: "Candidature non retenue ≠ refus définitif : elle est conservée en liste d'attente prioritaire et nous recontactons à chaque nouvelle vague, sans re-dépôt nécessaire.",
  },
  en: {
    ackErr: "You must accept the Terms and the Risk Disclosure.",
    selErr: "Please select your experience level and capital range.",
    okToast: "Application submitted!",
    dupErr: (em) => `An application with the email ${em} already exists. One submission per email.`,
    invalidErr: "Incomplete or invalid form. Please check all fields.",
    doneTitle: "Application received!", back: "Back", home: "Back to home",
    doneP1a: "We will reply within ", doneP1b: "48 hours", doneP1c: " to the email provided. Selected traders will receive a beta access link.",
    badge: "Invite-only program · Manual validation",
    h1: "Join the ANATRADEX beta program",
    intro1: "We are currently accepting a limited number of ", intro2: "semi-pro and professional traders", intro3: " to test ANATRADEX early, at early-bird pricing. Every application is manually reviewed within 48 hours. No credit card at this stage.",
    name: "Name (public or alias)", namePh: "E.g.: Alex M.",
    exp: "Experience level", capital: "Current trading account capital", select: "-- Select --",
    markets: "Primary markets (multiple choices)",
    broker: "Main broker/platform", brokerPh: "E.g.: IC Markets, OANDA, IBKR, FTMO, none...",
    why: "Why do you want to test ANATRADEX?", whyHint: "(2-3 sentences)",
    whyPh: "E.g.: I'm taking an FTMO challenge and want to validate my signals with robust backtesting + track my PBO to avoid overfitting.",
    ack1a: "I accept the ", terms: "Terms", privacy: "Privacy Policy", cookies: "Cookie Policy",
    ack2a: "I confirm I have read the ", riskDoc: "Risk Disclosure",
    ack2b: ". Trading can lead to a ", totalLoss: "total loss of capital",
    ack2c: ". I am at least 18 years old. ANATRADEX is an analysis tool, not investment advice.",
    submit: "Submit my application", footNote: "Reply within 48h. No spam. No credit card at this stage.",
    riskBanner1: "⚠️ WARNING", riskBanner2: " · ANATRADEX is a ", riskBanner3: "statistical analysis tool", riskBanner4: " and does NOT provide investment advice. Trading involves substantial risk of loss — 70-85% of retail traders lose money (ESMA). ANATRADEX is not a regulated investment firm and is not registered with any financial authority.",
    critTitle: "Who are we looking for? Our criteria, fully transparent",
    critIntro: "Each tester wave is composed to balance profiles. Here is exactly what we evaluate:",
    crit: [
      ["Trading experience", "From motivated beginners to professionals — we favor traders with at least 6 months of real practice."],
      ["Account capital", "Not an exclusion criterion: live accounts (even modest ones) give more concrete feedback than demo accounts."],
      ["Specific motivation", "2-3 concrete sentences about what you want to validate (prop firm challenge, strategy to verify, PBO tracking...) weigh more than a long vague text."],
      ["Platform diversity", "We look for a mix of MT4 / MT5 / cTrader / TradingView to test every import path."],
    ],
    critNote: "Not selected ≠ permanent rejection: your application is kept on a priority waitlist and we reach out at every new wave — no need to reapply.",
  },
};

export default function BetaApplication() {
  const [lang, setLang] = usePublicLang();
  const L = TXT[lang];
  const EXPERIENCE = EXP[lang];
  const CAPITAL_RANGE = CAP[lang];
  const PRIMARY_MARKET = MKT[lang];
  const [form, setForm] = useState({
    email: "", name: "", experience: "", capital: "", markets: [],
    broker: "", why: "", ackTerms: false, ackRisk: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleMarket = (m) => {
    setForm((f) => ({
      ...f,
      markets: f.markets.includes(m) ? f.markets.filter((x) => x !== m) : [...f.markets, m],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ackTerms || !form.ackRisk) {
      toast.error(L.ackErr);
      return;
    }
    if (!form.experience || !form.capital) {
      toast.error(L.selErr);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/beta-applications`, { ...form, lang });
      setDone(true);
      toast.success(L.okToast);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        toast.error(L.dupErr(form.email));
      } else if (status === 422) {
        toast.error(L.invalidErr);
      } else if (status === 400) {
        toast.error(detail || "Données invalides");
      } else {
        toast.error(detail || `Erreur ${status || ""}. Réessayez.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-bg text-white flex items-center justify-center p-6" data-testid="beta-app-done">
        <div className="max-w-lg text-center panel p-10">
          <CheckCircle2 className="text-up mx-auto mb-4" size={48} />
          <h1 className="font-heading text-3xl mb-3">{L.doneTitle}</h1>
          <p className="text-white/70 mb-6">
            {L.doneP1a}<b className="text-white">{L.doneP1b}</b>{L.doneP1c}
          </p>
          <p className="text-white/50 text-sm mb-8">
            <Link to="/academy" className="text-accent-blue underline">Academy</Link> · <Link to="/audit" className="text-accent-blue underline">Audit</Link>
          </p>
          <Link to="/" className="btn btn-primary text-sm px-6 py-3">{L.home}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-white" data-testid="beta-application-page">
      <div className="bg-red-950/60 border-b border-red-500/30 text-red-200 text-[11px] px-4 sm:px-6 py-2 text-center" data-testid="beta-risk-banner">
        <span className="font-semibold">{L.riskBanner1}</span>{L.riskBanner2}<b>{L.riskBanner3}</b>{L.riskBanner4}
      </div>
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Beta Application</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5">
              <ArrowLeft size={14} /> {L.back}
            </Link>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-up/10 border border-up/30 rounded-full text-xs uppercase tracking-widest text-up mb-6">
          <span className="w-2 h-2 rounded-full bg-up animate-pulse" /> {L.badge}
        </div>
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-4">
          {L.h1}
        </h1>
        <p className="text-white/60 mb-8 leading-relaxed">
          {L.intro1}<b className="text-white">{L.intro2}</b>{L.intro3}
        </p>

        <div className="mb-10 rounded-xl border border-accent-blue/30 bg-accent-blue/5 p-5 sm:p-6" data-testid="beta-criteria-panel">
          <h2 className="font-heading font-bold text-base md:text-lg mb-2">{L.critTitle}</h2>
          <p className="text-white/50 text-sm mb-4">{L.critIntro}</p>
          <ul className="space-y-2.5">
            {L.crit.map(([t, d]) => (
              <li key={t} className="flex items-start gap-2.5 text-sm">
                <CheckCircle2 size={15} className="text-accent-blue mt-0.5 shrink-0" />
                <span className="text-white/70"><b className="text-white">{t}</b> — {d}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-white/50 border-t border-white/10 pt-3" data-testid="beta-criteria-note">{L.critNote}</p>
        </div>

        <form onSubmit={submit} className="panel p-6 sm:p-8 space-y-6" data-testid="beta-application-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">{L.name}</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="field w-full"
                placeholder={L.namePh}
                data-testid="beta-name-input"
              />
            </div>
            <div>
              <label className="label">Email</label>
              <input
                required type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="field w-full"
                placeholder="vous@example.com"
                data-testid="beta-email-input"
              />
            </div>
          </div>

          <div>
            <label className="label">{L.exp}</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {EXPERIENCE.map((x) => (
                <label
                  key={x.v}
                  className={`cursor-pointer rounded-lg border p-3 text-sm transition ${
                    form.experience === x.v ? "border-accent-blue bg-accent-blue/10 text-white" : "border-white/10 bg-black/30 text-white/70 hover:border-white/30"
                  }`}
                  data-testid={`beta-exp-${x.v}`}
                >
                  <input
                    type="radio" name="experience" value={x.v}
                    checked={form.experience === x.v}
                    onChange={() => setForm({ ...form, experience: x.v })}
                    className="hidden"
                    required
                  />
                  {x.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="label">{L.capital}</label>
            <select
              required
              value={form.capital}
              onChange={(e) => setForm({ ...form, capital: e.target.value })}
              className="field w-full"
              data-testid="beta-capital-select"
            >
              <option value="">{L.select}</option>
              {CAPITAL_RANGE.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="label">{L.markets}</label>
            <div className="flex flex-wrap gap-2">
              {PRIMARY_MARKET.map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => toggleMarket(m)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    form.markets.includes(m) ? "border-accent-blue bg-accent-blue/20 text-white" : "border-white/10 bg-black/30 text-white/60 hover:border-white/30"
                  }`}
                  data-testid={`beta-market-${m.slice(0, 6)}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label">{L.broker}</label>
            <input
              value={form.broker}
              onChange={(e) => setForm({ ...form, broker: e.target.value })}
              className="field w-full"
              placeholder={L.brokerPh}
              data-testid="beta-broker-input"
            />
          </div>

          <div>
            <label className="label">{L.why} <span className="text-white/40">{L.whyHint}</span></label>
            <textarea
              required
              rows={4}
              value={form.why}
              onChange={(e) => setForm({ ...form, why: e.target.value })}
              className="field w-full resize-none"
              placeholder={L.whyPh}
              data-testid="beta-why-input"
            />
          </div>

          <div className="space-y-3 pt-4 border-t border-white/10">
            <label className="flex items-start gap-2.5 cursor-pointer text-xs text-white/70 leading-relaxed" data-testid="beta-ack-terms">
              <input
                type="checkbox"
                checked={form.ackTerms}
                onChange={(e) => setForm({ ...form, ackTerms: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-accent-blue shrink-0"
              />
              <span>
                {L.ack1a}<Link to="/terms" target="_blank" className="text-accent-blue underline">{L.terms}</Link>, <Link to="/privacy" target="_blank" className="text-accent-blue underline">{L.privacy}</Link>, <Link to="/cookies" target="_blank" className="text-accent-blue underline">{L.cookies}</Link>.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer text-xs text-white/70 leading-relaxed" data-testid="beta-ack-risk">
              <input
                type="checkbox"
                checked={form.ackRisk}
                onChange={(e) => setForm({ ...form, ackRisk: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-accent-blue shrink-0"
              />
              <span>
                {L.ack2a}<Link to="/risk-disclosure" target="_blank" className="text-warn underline">{L.riskDoc}</Link>{L.ack2b}<b className="text-warn">{L.totalLoss}</b>{L.ack2c}
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || !form.ackTerms || !form.ackRisk}
            className="btn btn-primary w-full justify-center py-3.5 text-base disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="beta-submit-btn"
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {L.submit}
          </button>
          <p className="text-center text-xs text-white/40">
            {L.footNote}
          </p>
        </form>
      </div>
    </div>
  );
}
